import { ApolloCache } from '@apollo/client';
import {
  AgentComplianceReviewByShareLinkTokenDocument,
  AgentComplianceReviewDocumentDocument,
  useAgentComplianceReviewIgnoreSuggestionMutation,
} from '@client/graphql/__generated__/main-operations';
import {
  AgentComplianceReviewByShareLinkTokenQuery,
  AgentComplianceReviewDocumentQuery,
  AgentComplianceReviewIgnoreSuggestionInput,
} from '@client/graphql/__generated__/types';
import { useCallback } from 'react';

interface UseAgentComplianceReviewIgnoreSuggestionArgs {
  /** Share-link token (`?token=` query param). */
  token: string | undefined;
  /** Folder UUID (route param) the review belongs to. */
  ingestionFolderUuid: string | undefined;
}

/**
 * The per-call portion of the ignore input — the token + folder are bound by
 * the hook, so callers only supply what identifies the suggestion being hidden
 * (a document-level suggestion via `contractDocumentUuid`/`ruleUuid`/`pageNumber`
 * or a folder-level one via `ingestionFolderValidationId`/`documentForm`/…).
 */
export type IgnoreSuggestionArgs = Omit<
  AgentComplianceReviewIgnoreSuggestionInput,
  'token' | 'ingestionFolderUuid' | 'message'
> & {
  /**
   * The agent's reason for ignoring: text to store it, `null` to clear a reason
   * left by an earlier ignore, omitted to leave whatever is stored alone (what
   * Undo does).
   *
   * `null` cannot travel on the wire — codegen maps nullable inputs to
   * `T | undefined` (see `maybeValue` in `graphql/codegen.ts`), so an explicit
   * null is indistinguishable from omission by the time it reaches the server.
   * It is sent as an empty string instead, which the document service
   * normalizes back to null before writing.
   */
  message?: string | null;
};

interface UseAgentComplianceReviewIgnoreSuggestionResult {
  loading: boolean;
}

/**
 * Patches the open document detail's `hiddenValidationReviews` in the Apollo
 * cache for a document-level ignore/un-ignore, so the suggestion crosses out
 * (or un-crosses) instantly without refetching the heavy detail query (which
 * would reload the PDF). Ignoring does not change the server-side blocking
 * count, so the overview is left untouched here.
 */
const patchDocumentHiddenReviews = (
  cache: ApolloCache<unknown>,
  input: AgentComplianceReviewIgnoreSuggestionInput
): void => {
  if (input.ingestionDocumentId == null) {
    return;
  }

  cache.updateQuery<AgentComplianceReviewDocumentQuery>(
    {
      query: AgentComplianceReviewDocumentDocument,
      variables: {
        input: {
          token: input.token,
          ingestionFolderUuid: input.ingestionFolderUuid,
          ingestionDocumentId: input.ingestionDocumentId,
        },
      },
    },
    (data) => {
      const detail = data?.agentComplianceReviewDocument;
      if (!detail) {
        return data;
      }

      const reviews = detail.hiddenValidationReviews ?? [];
      // Match the way the client derives the ignored state: rule + contract
      // document (the validation id is the same across the open detail).
      const matches = (review: (typeof reviews)[number]): boolean =>
        (review.ruleUuid ?? null) === (input.ruleUuid ?? null) &&
        (review.contractDocumentUuid ?? null) ===
          (input.contractDocumentUuid ?? null);

      const isHidden = input.isHidden ?? true;
      let hiddenValidationReviews: typeof reviews;
      if (isHidden) {
        hiddenValidationReviews = reviews.some(matches)
          ? reviews
          : [
              ...reviews,
              {
                __typename: 'AgentComplianceReviewHiddenReviewPayload' as const,
                // Synthetic until the next real fetch; the display matches on
                // rule/contract-document, not this uuid.
                reviewUuid: `optimistic-${input.ruleUuid ?? ''}-${
                  input.contractDocumentUuid ?? ''
                }`,
                ruleUuid: input.ruleUuid ?? undefined,
                contractDocumentUuid: input.contractDocumentUuid ?? undefined,
                pageNumber: input.pageNumber ?? undefined,
                ingestionDocumentValidationId:
                  input.ingestionDocumentValidationId ?? undefined,
                source: 'AGENT',
                // Surface the reason immediately (hover tooltip) before the
                // next real fetch confirms it. '' is the cleared-reason signal
                // and must read as "no reason" rather than an empty tooltip.
                message: input.message === '' ? undefined : input.message,
              },
            ];
      } else {
        hiddenValidationReviews = reviews.filter((review) => !matches(review));
      }

      return {
        ...data,
        agentComplianceReviewDocument: { ...detail, hiddenValidationReviews },
      };
    }
  );
};

/**
 * Toggles `isIgnored` on the matching overview folder suggestion in the Apollo
 * cache for a folder-level ignore/un-ignore, so it crosses out (or un-crosses)
 * instantly without refetching the overview.
 */
const patchFolderSuggestion = (
  cache: ApolloCache<unknown>,
  input: AgentComplianceReviewIgnoreSuggestionInput
): void => {
  if (input.ingestionFolderValidationId == null) {
    return;
  }

  cache.updateQuery<AgentComplianceReviewByShareLinkTokenQuery>(
    {
      query: AgentComplianceReviewByShareLinkTokenDocument,
      variables: {
        input: {
          token: input.token,
          ingestionFolderUuid: input.ingestionFolderUuid,
        },
      },
    },
    (data) => {
      const overview = data?.agentComplianceReviewByShareLinkToken;
      if (!overview) {
        return data;
      }

      const isHidden = input.isHidden ?? true;
      const folderSuggestions = overview.folderSuggestions.map((suggestion) => {
        const matches =
          (suggestion.ingestionFolderValidationId ?? null) ===
            (input.ingestionFolderValidationId ?? null) &&
          (suggestion.ruleUuid ?? null) === (input.ruleUuid ?? null) &&
          (suggestion.documentCategory ?? null) ===
            (input.documentCategory ?? null) &&
          (suggestion.documentForm ?? null) === (input.documentForm ?? null) &&
          (suggestion.documentName ?? null) === (input.documentName ?? null);

        return matches ? { ...suggestion, isIgnored: isHidden } : suggestion;
      });

      return {
        ...data,
        agentComplianceReviewByShareLinkToken: {
          ...overview,
          folderSuggestions,
        },
      };
    }
  );
};

/**
 * Wraps the token-bound `agentComplianceReviewIgnoreSuggestion` mutation for the
 * agent compliance review page. Binds the share-link token + folder uuid so
 * callers only pass the suggestion-identifying fields.
 *
 * Rather than refetching (which would reload the PDF/overview on every click),
 * it patches the Apollo cache in place: a document-level ignore toggles the open
 * detail's `hiddenValidationReviews`; a folder-level ignore toggles the matching
 * overview suggestion's `isIgnored`. Both make the cross-out instant.
 *
 * @returns `[ignore, { loading }]` mirroring the apollo mutation tuple.
 */
export const useAgentComplianceReviewIgnoreSuggestion = ({
  token,
  ingestionFolderUuid,
}: UseAgentComplianceReviewIgnoreSuggestionArgs): [
  (args: IgnoreSuggestionArgs) => Promise<void>,
  UseAgentComplianceReviewIgnoreSuggestionResult,
] => {
  const [mutate, { loading }] =
    useAgentComplianceReviewIgnoreSuggestionMutation({
      update: (cache, _result, { variables }) => {
        const input = variables?.input;
        if (!input) {
          return;
        }
        patchDocumentHiddenReviews(cache, input);
        patchFolderSuggestion(cache, input);
      },
    });

  const ignore = useCallback(
    async (args: IgnoreSuggestionArgs): Promise<void> => {
      // Never fire the write with empty-string fallback identifiers — a missing
      // token/folder is a not-ready state, not a valid request to send.
      if (
        token == null ||
        token === '' ||
        ingestionFolderUuid == null ||
        ingestionFolderUuid === ''
      ) {
        return;
      }
      const { message, ...rest } = args;
      await mutate({
        variables: {
          input: {
            token,
            ingestionFolderUuid,
            ...rest,
            // null (clear the reason) becomes '' because the generated input
            // cannot express null; omission still means "leave it alone".
            ...(message !== undefined ? { message: message ?? '' } : {}),
          },
        },
      });
    },
    [mutate, token, ingestionFolderUuid]
  );

  return [ignore, { loading }];
};
